import isNumber from 'lodash.isnumber';

import {Separator} from '../constants';
import {CurrencySymbols} from '../currency/currency-symbols';
import {convertToDigit} from '../utils';

import type {Digit} from '../constants';
import type {NumberFormat} from '../number';
import type {Money} from './typedef';

type Options = {
    suffix?: string,
    roundOff?: boolean,
    truncateDecimals?: boolean,
    hideTrailingZeros?: boolean,
    decimals?: number,
    threeDigit?: boolean,
    showZeros?: boolean,
    absoluteValue?: boolean,
    negativeParenthesis?: boolean,
    native?: boolean,
    showDigit?: boolean,
    hideSymbol?: boolean,
    preventScale?: boolean,
    valueAbbreviation?: string,
    fromDigit?: Digit,
    toDigit?: Digit,
};

export type Locale = {
    decimalFormat: string,
    thousandFormat: string,
    currency: string,
    symbolPosition: string,
};

const formatNegativeParenthesis = (
    value: number,
    formattedValue: string,
    negativeParenthesis: boolean = false,
) => {
    if (value >= 0) {
        return formattedValue;
    }

    return negativeParenthesis ? `(${formattedValue})` : `-${formattedValue}`;
};

/**
 * @name MoneyFormat
 * @param {NumberFormat=} numberFormat
 * @param {Digit=} digit - literal for the number digit
 */
export class MoneyFormat {
    numberFormat: NumberFormat;
    digit: Digit | null | undefined;

    constructor(numberFormat: NumberFormat, digit?: Digit) {
        this.numberFormat = numberFormat;
        this.digit = digit;
    }

    /**
     * @description Returns the user's locale-based formatted data for money input
     * @param {Money=} amount
     * @param {Locale} locale - user's locale
     * @param {Options} [options={}] - format options
     * @return {string} - formatted value or empty string
     */
    format(
        amount: Money | undefined,
        locale?: Locale,
        options: Options = {},
    ) {
        if (
            amount === undefined ||
            (amount.amount === 0 && !options.showZeros) ||
            !locale
        ) {
            return '';
        }

        const mergedOptions = {
            ...options,
            absoluteValue: true,
            negativeParenthesis: false,
        };
        let value = options.native ? amount.nativeAmount : amount.amount;
        let digit = '';

        if (
            this.digit !== undefined &&
            this.digit !== null &&
            value &&
            isNumber(value)
        ) {
            const convertOptions = {
                value,
                toDigit: this.digit,
                fromDigit: mergedOptions.fromDigit,
                preventScale: mergedOptions.preventScale,
            } as const;
            const converted = convertToDigit(convertOptions);
            digit = options.showDigit !== false ? converted.digit : '';
            value = converted.value;
        }

        let formatted =
            this.numberFormat.format(value, locale, mergedOptions) +
            (options.valueAbbreviation || digit);
        const currentCurrency = amount.currency || locale.currency;

        if (!options.hideSymbol && formatted) {
            const symbol =
                CurrencySymbols[
                    options.native && amount.nativeCurrency
                        ? amount.nativeCurrency
                        : currentCurrency
                ] || '';
            formatted =
                locale.symbolPosition === 'LEFT'
                    ? `${symbol}${formatted}`
                    : `${formatted}${Separator.SPACE}${symbol}`;
        }

        return formatNegativeParenthesis(
            amount.amount as number,
            formatted,
            options.negativeParenthesis,
        );
    }
}
